// Incident reporting (docs/plans/phase3-operations-modules.md §1): checking a report, the
// review workflow, overdue actions and who sees the reporter. Pure: callers pass the time.

import { err, ok, type Result } from '../result.ts';
import { compareDays, eatDayOf, parseIsoDay } from '../time.ts';

export const INCIDENT_STATUSES = [
  'reported',
  'under_review',
  'actions',
  'closed',
  'entered_in_error',
] as const;
export type IncidentStatus = (typeof INCIDENT_STATUSES)[number];

export const INCIDENT_STATUS_LABELS: Record<IncidentStatus, string> = {
  reported: 'Reported',
  under_review: 'Under review',
  actions: 'Actions open',
  closed: 'Closed',
  entered_in_error: 'Entered in error',
};

/** Statuses still being worked on: the register's "Open" tab. */
export const OPEN_INCIDENT_STATUSES: readonly IncidentStatus[] = [
  'reported',
  'under_review',
  'actions',
];

export const INCIDENT_ACTION_STATUSES = ['open', 'done', 'cancelled'] as const;
export type IncidentActionStatus = (typeof INCIDENT_ACTION_STATUSES)[number];

export const INCIDENT_ACTION_STATUS_LABELS: Record<IncidentActionStatus, string> = {
  open: 'Open',
  done: 'Done',
  cancelled: 'Cancelled',
};

export const INCIDENT_SOURCES = ['staff_report', 'patient_feedback'] as const;
export type IncidentSource = (typeof INCIDENT_SOURCES)[number];

export const INCIDENT_EVENT_KINDS = [
  'reported',
  'review_started',
  'root_cause_recorded',
  'action_added',
  'action_done',
  'action_cancelled',
  'actions_started',
  'closed',
  'reopened',
  'entered_in_error',
] as const;
export type IncidentEventKind = (typeof INCIDENT_EVENT_KINDS)[number];

export const INCIDENT_EVENT_LABELS: Record<IncidentEventKind, string> = {
  reported: 'Reported',
  review_started: 'Review started',
  root_cause_recorded: 'Root cause recorded',
  action_added: 'Action added',
  action_done: 'Action done',
  action_cancelled: 'Action cancelled',
  actions_started: 'Actions started',
  closed: 'Closed',
  reopened: 'Reopened for review',
  entered_in_error: 'Entered in error',
};

export const INCIDENT_TEXT_LIMITS = {
  location: 120,
  description: 4000,
  immediateAction: 2000,
  equipmentText: 120,
  rootCause: 4000,
  note: 2000,
  actionDescription: 500,
} as const;

/** A report can't be in the future; a few minutes cover clock differences between devices. */
const CLOCK_SKEW_MS = 5 * 60 * 1000;

export type IncidentErrorCode =
  | 'incident.occurred_in_future'
  | 'incident.location_required'
  | 'incident.description_required'
  | 'incident.text_too_long'
  | 'incident.not_allowed'
  | 'incident.root_cause_required'
  | 'incident.action_required'
  | 'incident.actions_open';

export const INCIDENT_ERROR_MESSAGES: Record<IncidentErrorCode, string> = {
  'incident.occurred_in_future':
    'The time is in the future. Enter when the incident happened, in East Africa Time.',
  'incident.location_required': 'Say where it happened.',
  'incident.description_required': 'Say what happened.',
  'incident.text_too_long': 'That text is too long. Shorten it and save again.',
  'incident.not_allowed':
    "That step doesn't fit this incident now. Reload it to see its status, then try again.",
  'incident.root_cause_required': 'Record the root cause before starting actions.',
  'incident.action_required': 'Add at least one action, with an owner and a due date.',
  'incident.actions_open': 'Finish or cancel every open action before closing the incident.',
};

export interface IncidentReportInput {
  occurredAt: Date;
  location: string;
  description: string;
  immediateAction: string | null;
  equipmentText: string | null;
}

function optional(text: string | null): string | null {
  const trimmed = text?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

function tooLong(text: string | null, limit: number): boolean {
  return text !== null && text.length > limit;
}

/** Trims the report's text and checks the time and lengths. */
export function checkIncidentReport(
  input: IncidentReportInput,
  now: Date,
): Result<IncidentReportInput, IncidentErrorCode> {
  if (input.occurredAt.getTime() > now.getTime() + CLOCK_SKEW_MS)
    return err('incident.occurred_in_future');
  const location = input.location.trim();
  const description = input.description.trim();
  if (location === '') return err('incident.location_required');
  if (description === '') return err('incident.description_required');
  const immediateAction = optional(input.immediateAction);
  const equipmentText = optional(input.equipmentText);
  const L = INCIDENT_TEXT_LIMITS;
  if (
    tooLong(location, L.location) ||
    tooLong(description, L.description) ||
    tooLong(immediateAction, L.immediateAction) ||
    tooLong(equipmentText, L.equipmentText)
  )
    return err('incident.text_too_long');
  return ok({
    occurredAt: input.occurredAt,
    location,
    description,
    immediateAction,
    equipmentText,
  });
}

/** The steps a reviewer takes. `reopened` lands in `under_review`. */
export type IncidentStep = 'under_review' | 'actions' | 'closed' | 'reopened' | 'entered_in_error';

export interface IncidentProgress {
  /** Actions not yet done or cancelled. */
  openActions: number;
  /** All actions that are not cancelled. */
  actions: number;
  hasRootCause: boolean;
}

/**
 * Whether a step is allowed from a status, and the status it leads to (plan §1.1):
 * reported → under review → actions → closed; under review may close straight away; a
 * closed incident is reopened for review; entered in error only before actions start.
 */
export function incidentTransition(
  from: IncidentStatus,
  step: IncidentStep,
  progress: IncidentProgress,
): Result<IncidentStatus, IncidentErrorCode> {
  switch (step) {
    case 'under_review':
      return from === 'reported' ? ok('under_review') : err('incident.not_allowed');
    case 'actions':
      if (from !== 'under_review') return err('incident.not_allowed');
      if (!progress.hasRootCause) return err('incident.root_cause_required');
      if (progress.actions === 0) return err('incident.action_required');
      return ok('actions');
    case 'closed':
      if (from !== 'under_review' && from !== 'actions') return err('incident.not_allowed');
      if (progress.openActions > 0) return err('incident.actions_open');
      return ok('closed');
    case 'reopened':
      return from === 'closed' ? ok('under_review') : err('incident.not_allowed');
    case 'entered_in_error':
      return from === 'reported' || from === 'under_review'
        ? ok('entered_in_error')
        : err('incident.not_allowed');
  }
}

/** An open action is overdue from the day after its due date, in East Africa Time. */
export function incidentActionIsOverdue(
  action: { status: IncidentActionStatus; dueOn: string },
  now: Date,
): boolean {
  if (action.status !== 'open') return false;
  const due = parseIsoDay(action.dueOn);
  if (!due.ok) return false;
  return compareDays(eatDayOf(now), due.value) > 0;
}

/** The reporter is shown unless they asked to stay anonymous; the safety lead always sees it. */
export function reporterShown(incident: { anonymous: boolean }, reader: { manage: boolean }) {
  return !incident.anonymous || reader.manage;
}

/** The register's readers see every incident; anyone sees the ones they reported. */
export function mayReadIncident(
  incident: { reportedBy: string },
  reader: { userId: string; read: boolean },
): boolean {
  return reader.read || incident.reportedBy === reader.userId;
}
